import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../../components/shop/ProductCard';

const CONDITIONS = ['excellent','very good','good','fair','poor'];

export default function ShopPage() {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const search      = searchParams.get('search') || '';
  const subcategory = searchParams.get('sub') || '';
  const condition   = searchParams.get('condition') || '';
  const minPrice    = searchParams.get('min') || '';
  const maxPrice    = searchParams.get('max') || '';
  const newArrivals = searchParams.get('new_arrivals') === '1';
  const featured    = searchParams.get('featured') === '1';
  const LIMIT = 24;

  useEffect(() => {
    axios.get('/api/categories').then(r => {
      setCategories(r.data);
      if (categorySlug) setActiveCategory(r.data.find(c => c.slug === categorySlug) || null);
    }).catch(() => {});
  }, [categorySlug]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: LIMIT };
    if (categorySlug) params.category = categorySlug;
    if (subcategory)  params.subcategory = subcategory;
    if (search)       params.search = search;
    if (condition)    params.condition = condition;
    if (minPrice)     params.min_price = minPrice;
    if (maxPrice)     params.max_price = maxPrice;
    if (newArrivals)  params.new_arrivals = 1;
    if (featured)     params.featured = 1;

    axios.get('/api/products', { params }).then(r => {
      setProducts(r.data.products || []);
      setTotal(r.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [categorySlug, subcategory, search, condition, minPrice, maxPrice, newArrivals, featured, page]);

  useEffect(() => { setPage(1); }, [categorySlug, search, subcategory, condition, minPrice, maxPrice]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val) next.set(key, val); else next.delete(key);
    setSearchParams(next);
  };

  const totalPages = Math.ceil(total / LIMIT);

  const pageTitle = search ? `Search: "${search}"` : activeCategory?.name || 'All Inventory';

  return (
    <div>
      <div className="page-hero" style={{ padding: '36px 0' }}>
        <div className="container">
          <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>{pageTitle}</h1>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14 }}>{total} items available</p>
          {/* Breadcrumb */}
          <div style={{ marginTop: 10, fontSize: 13, color: 'rgba(255,255,255,0.5)', display: 'flex', gap: 6, alignItems: 'center' }}>
            <Link to="/" style={{ color: 'rgba(255,255,255,0.5)' }}>Home</Link> /
            <Link to="/shop" style={{ color: 'rgba(255,255,255,0.5)' }}>Shop</Link>
            {activeCategory && <><span>/</span><span style={{ color: '#c9a227' }}>{activeCategory.name}</span></>}
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '32px 20px' }}>
        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>

          {/* Sidebar */}
          <aside style={{ width: 230, flexShrink: 0, position: 'sticky', top: 80 }}>
            {/* Search */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2744', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Search</div>
              <form onSubmit={e => { e.preventDefault(); setFilter('search', e.target.search.value); }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input name="search" defaultValue={search} placeholder="Keywords..." className="form-control" style={{ fontSize: 13 }} />
                  <button type="submit" className="btn btn-primary btn-sm">Go</button>
                </div>
              </form>
            </div>

            {/* Categories */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2744', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Categories</div>
              <Link to="/shop" style={{ display: 'block', padding: '6px 0', fontSize: 13, color: !categorySlug ? '#c9a227' : '#555', fontWeight: !categorySlug ? 700 : 400 }}>All Items ({categories.reduce((s,c) => s + (c.product_count||0), 0)})</Link>
              {categories.map(cat => (
                <div key={cat.id}>
                  <Link to={`/shop/${cat.slug}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: categorySlug === cat.slug ? '#c9a227' : '#555', fontWeight: categorySlug === cat.slug ? 700 : 400 }}>
                    <span>{cat.icon} {cat.name}</span>
                    <span style={{ color: '#9a9a9a', fontSize: 11 }}>{cat.product_count}</span>
                  </Link>
                  {categorySlug === cat.slug && cat.subcategories?.length > 0 && (
                    <div style={{ paddingLeft: 12, borderLeft: '2px solid #c9a227', marginLeft: 8, marginBottom: 4 }}>
                      {cat.subcategories.map(sub => (
                        <button key={sub.id} onClick={() => setFilter('sub', subcategory === sub.slug ? '' : sub.slug)}
                          style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '5px 0', fontSize: 12, color: subcategory === sub.slug ? '#c9a227' : '#666', fontWeight: subcategory === sub.slug ? 700 : 400, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                          <span>{sub.name}</span>
                          <span style={{ color: '#9a9a9a' }}>{sub.product_count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Price */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2744', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price Range</div>
              <form onSubmit={e => { e.preventDefault(); setFilter('min', e.target.min.value); setFilter('max', e.target.max.value); }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <input name="min" defaultValue={minPrice} placeholder="Min" type="number" className="form-control" style={{ fontSize: 13 }} />
                  <span style={{ color: '#9a9a9a' }}>–</span>
                  <input name="max" defaultValue={maxPrice} placeholder="Max" type="number" className="form-control" style={{ fontSize: 13 }} />
                </div>
                <button type="submit" className="btn btn-outline btn-sm" style={{ width: '100%' }}>Apply</button>
              </form>
            </div>

            {/* Condition */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2744', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Condition</div>
              {CONDITIONS.map(c => (
                <button key={c} onClick={() => setFilter('condition', condition === c ? '' : c)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 0', fontSize: 13, background: 'none', border: 'none', color: condition === c ? '#c9a227' : '#555', fontWeight: condition === c ? 700 : 400, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {condition === c ? '✓ ' : ''}{c}
                </button>
              ))}
            </div>
          </aside>

          {/* Product grid */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Active filters */}
            {(search || subcategory || condition || minPrice || maxPrice) && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {search && <span className="badge badge-navy" style={{ cursor: 'pointer' }} onClick={() => setFilter('search','')}>"{search}" ✕</span>}
                {subcategory && <span className="badge badge-navy" style={{ cursor: 'pointer' }} onClick={() => setFilter('sub','')}>Sub: {subcategory} ✕</span>}
                {condition && <span className="badge badge-navy" style={{ cursor: 'pointer', textTransform: 'capitalize' }} onClick={() => setFilter('condition','')}>Condition: {condition} ✕</span>}
                {(minPrice || maxPrice) && <span className="badge badge-navy" style={{ cursor: 'pointer' }} onClick={() => { setFilter('min',''); setFilter('max',''); }}>Price: ${minPrice||'0'}–${maxPrice||'∞'} ✕</span>}
              </div>
            )}

            {loading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : products.length === 0 ? (
              <div className="empty-state">
                <div style={{ fontSize: 48 }}>🔍</div>
                <p>No items found. Try adjusting your filters.</p>
                <Link to="/shop" className="btn btn-navy" style={{ marginTop: 16 }}>Clear Filters</Link>
              </div>
            ) : (
              <>
                <div className="grid-auto">
                  {products.map(p => <ProductCard key={p.uuid} product={p} />)}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 32, flexWrap: 'wrap' }}>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn btn-outline btn-sm">← Prev</button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => setPage(p)} className={`btn btn-sm ${page === p ? 'btn-navy' : 'btn-outline'}`}>{p}</button>
                    ))}
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="btn btn-outline btn-sm">Next →</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
